import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { ApiError } from '../api/client';
import { listRondoPayoffs, type RondoPayoffItem } from '../api/rondo';
import { ServiceBar } from '../shell/ServiceBar';
import { Button } from '../components/arc/button/button';
import { Dialog, DialogContent } from '../components/arc/dialog/dialog';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import { useRondoNav } from './RondoNav';
import './users-seznam.css';
import './users-vyplaty.css';

const COUNTRY_FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };
const STATUS: Record<RondoPayoffItem['status'], string> = {
  new: 'Nové', pending: 'Čekající', sent: 'Odesláno', delivered: 'Doručeno', rejected: 'Zamítnuto',
};
const money = (n: number) => n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const intFmt = (n: number) => n.toLocaleString('cs-CZ');

function initials(name: string) {
  const words = name.split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase();
}

function dateTime(iso: string | null) {
  return iso ? new Date(iso).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
}

export function UsersVyplatyPage() {
  const [query, setQuery] = useState('');
  const [country, setCountry] = useState('all');
  const [status, setStatus] = useState('all');
  const [limit, setLimit] = useState(100);
  const [rows, setRows] = useState<RondoPayoffItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<RondoPayoffItem | null>(null);
  const nav = useRondoNav();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      listRondoPayoffs({
        search: query.trim() || undefined,
        country: country !== 'all' ? country : undefined,
        status: status !== 'all' ? status : undefined,
        limit,
      })
        .then((result) => {
          if (cancelled) return;
          setRows(result.items);
          setTotal(result.total);
          setError(null);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setError(e instanceof ApiError ? e.message : 'Načtení selhalo');
          setRows([]);
          setTotal(0);
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, country, status, limit]);

  const tableRows = rows.map((row) => ({ ...row, detail: row.id }));
  type TableRow = (typeof tableRows)[number];
  const columns: DataColumn<TableRow>[] = [
    { key: 'name', label: 'Uživatel', width: 190, render: (_, row) => <button type="button" className="rondo-arc-user-link" onClick={() => setSelected(row)}><span className="rondo-arc-user-avatar">{initials(row.name)}</span><span>{row.name}</span></button> },
    { key: 'email', label: 'Email', width: 190, render: (_, row) => row.email || '—' },
    { key: 'requestedAt', label: 'Datum žádosti', width: 150, render: (_, row) => dateTime(row.requestedAt) },
    { key: 'amount', label: 'Částka', numeric: true, width: 130, render: (_, row) => `${money(row.amount)} ${row.currency}` },
    { key: 'country', label: 'Země', width: 85, render: (_, row) => <span className="rondo-arc-user-country">{COUNTRY_FLAG[row.country] ?? ''} {row.country}</span> },
    { key: 'status', label: 'Stav', width: 120, render: (_, row) => <span className="rondo-arc-payoff-status" data-status={row.status}>{STATUS[row.status]}</span> },
    { key: 'detail', label: '', width: 48, sortable: false, render: (_, row) => <button type="button" className="rondo-arc-user-detail" aria-label={`Detail výplaty ${row.name}`} onClick={() => setSelected(row)}><ChevronRight size={16} aria-hidden="true" /></button> },
  ];
  const waiting = rows.filter((row) => row.status === 'new' || row.status === 'pending').length;
  const completed = rows.filter((row) => row.status === 'sent' || row.status === 'delivered').length;
  const markets = new Set(rows.map((row) => row.country)).size;

  return <section className="rondo-arc-users-list rondo-arc-payoffs p1-arc-scope">
    <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">UŽIVATELÉ</div><h1>Výplaty</h1><p>Žádosti uživatelů a stav jejich zpracování</p></div></header>
    <div className="rondo-arc-users-list-metrics">
      <MetricCard label="Žádosti celkem" value={total} context="Podle aktuálního filtru" />
      <MetricCard label="Čeká na zpracování" value={waiting} context="Na načtené stránce" />
      <MetricCard label="Odesláno nebo doručeno" value={completed} context="Na načtené stránce" />
      <MetricCard label="Zastoupené trhy" value={markets} context="Na načtené stránce" />
    </div>
    <ServiceBar
      selects={[
        { label: 'Země', value: country, onChange: setCountry, options: [
          { value: 'all', label: 'Všechny' }, { value: 'CZ', label: '🇨🇿 CZ - Česko' },
          { value: 'SK', label: '🇸🇰 SK - Slovensko' }, { value: 'RO', label: '🇷🇴 RO - Rumunsko' } ] },
        { label: 'Stav', value: status, onChange: setStatus, options: [
          { value: 'all', label: 'Všechny' }, { value: 'new', label: 'Nové' }, { value: 'pending', label: 'Čekající' },
          { value: 'sent', label: 'Odesláno' }, { value: 'delivered', label: 'Doručeno' }, { value: 'rejected', label: 'Zamítnuto' } ] },
        { label: 'Limit', value: String(limit), onChange: (value) => setLimit(Number(value)), options: [
          { value: '25', label: '25' }, { value: '50', label: '50' }, { value: '100', label: '100' } ] },
      ]}
      onReset={() => { setQuery(''); setCountry('all'); setStatus('all'); setLimit(100); }}
      search={query}
      onSearch={setQuery}
      searchPlaceholder="jméno / email / userId…"
      variant="seg"
    />
    <div className="rondo-arc-users-list-table rondo-arc-payoffs-table">
      {error && <p role="alert" className="rondo-arc-users-error">{error}</p>}
      {loading ? <p className="rondo-arc-users-loading">Načítám výplaty…</p> : <SortableDataTable rows={tableRows} columns={columns} rowKey="id" caption="Výplaty uživatelů Ronda" emptyMessage="Žádná výplata neodpovídá filtru." />}
    </div>
    <p className="rondo-arc-payoffs-count">{intFmt(rows.length)} zobrazených žádostí{total > rows.length ? ` z ${intFmt(total)}` : ''}</p>
    <Dialog open={selected !== null} onOpenChange={(open) => { if (!open) setSelected(null); }}>
      {selected && <DialogContent title={`Výplata #${selected.id}`} description={selected.name} className="rondo-arc-payoff-dialog">
        <div className="rondo-arc-payoff-detail p1-arc-scope">
          <MetricCard label="Částka k výplatě" value={selected.amount} decimals={2} suffix={`\u00a0${selected.currency}`} context={`Žádost ${dateTime(selected.requestedAt)}`} />
          <span className="rondo-arc-payoff-status" data-status={selected.status}>{STATUS[selected.status]}</span>
          <dl>
            <div><dt>Uživatel</dt><dd>{selected.name}</dd></div>
            <div><dt>Email</dt><dd>{selected.email || '—'}</dd></div>
            <div><dt>Země</dt><dd>{COUNTRY_FLAG[selected.country] ?? ''} {selected.country}</dd></div>
            <div><dt>Datum žádosti</dt><dd>{dateTime(selected.requestedAt)}</dd></div>
            <div><dt>Měna</dt><dd>{selected.currency}</dd></div>
            <div><dt>Rondo userId</dt><dd>{selected.rondoUserId ?? '—'}</dd></div>
          </dl>
          {selected.currencyMismatch && <p role="alert" className="rondo-arc-payoff-warning">Měna výplaty neodpovídá zemi uživatele.</p>}
          <div className="rondo-arc-payoff-actions">
            <Button variant="secondary" size="sm" onClick={() => setSelected(null)}>Zavřít</Button>
            {selected.rondoUserId != null && <Button variant="primary" size="sm" onClick={() => { const userId = selected.rondoUserId; setSelected(null); if (userId != null) nav.openUserDetail(userId); }}>Otevřít uživatele</Button>}
          </div>
        </div>
      </DialogContent>}
    </Dialog>
  </section>;
}
